import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import DefaultImg from "../../images/no_profile.png";
import { useDispatch, useSelector } from "react-redux";
import { update_user, approved_user } from "../../reduxData/user/userAction";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import { Box } from "@mui/joy";
import { formatDateTime } from "../../hooks/formatDateTime";
import { useTranslation } from "react-i18next";
import UserStrike from "../../components/UserStrike";

const { REACT_APP_BASE_URL, REACT_APP_IMAGE_URL } = process.env;

const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="10" fill="#22c55e" />
        <path d="M6 10.5l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CrossIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="10" fill="#ef4444" />
        <path d="M7 7l6 6M13 7l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const WarnIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2L18.66 17H1.34L10 2z" fill="#f59e0b" />
        <path d="M10 8v4M10 13.5v.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const ChevronIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M4 6l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Temporarily hides the Approve / Reject seller actions. Set to true to bring them back.
const SHOW_SELLER_REVIEW_ACTIONS = false;

const STRIPE_STATUSES =["NOT_CONNECTED", "PENDING", "IN_REVIEW", "FAILED", "REJECTED", "ENABLED"];
const STRIPE_BADGE_CLASS = {
    NOT_CONNECTED: "udr-req-badge--missing",
    PENDING: "udr-req-badge--pending",
    IN_REVIEW: "udr-req-badge--pending",
    FAILED: "udr-req-badge--missing",
    REJECTED: "udr-req-badge--missing",
    ENABLED: "udr-req-badge--done",
};

const UserDetails = () => {
    const { t } = useTranslation();
    const [user, setUser] = useState({});
    const { userPermissions } = useSelector(state => state.auth);
    const { restrictionData } = useSelector(state => state.user);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [sellerRejectReason, setSellerRejectReason] = useState("");
    const [rejectReasonModal, setRejectReasonModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const userDetail = location?.state?.userDetail;
        if (userDetail) setUser(userDetail);
    }, [location]);

    if (!user || Object.keys(user).length === 0) {
        return (
            <div className="udr-page">
                <div className="udr-empty">
                    <h3>{t("userDetails.noDataTitle")}</h3>
                    <button onClick={() => navigate("/users")}>{t("userDetails.noDataBack")}</button>
                </div>
            </div>
        );
    }

    const isSeller = user.role?.toLowerCase() === "seller";
    const showCompanyLogo = isSeller && user.promoted === true && user.company_logo;

    const handleApprove = async (status) => {
        setIsLoading(true);
        try {
            const formData = {
                seller_status: status,
                ...(status === "SELLER_REJECTED" && { rejected_reason: sellerRejectReason }),
            };
            await approved_user(user?.id, formData, navigate, dispatch);
        } catch (error) {

        } finally {
            setIsLoading(false);
        }
    };

    const statusClassMap = {
        SELLER_REJECTED: 'cancelled',
        SELLER_REGISTERED: 'pending',
        SELLER_ACTIVE: 'approved',
        SELLER_EMAIL_VERIFIED: 'approved',
        BUYER_VERIFIED: 'approved',
        BUYER_REGISTERED: 'pending',
    };

    const formatStatus = (status = "") => {
        return status
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const restrictions = Array.isArray(restrictionData) ? restrictionData : restrictionData ? [restrictionData] : [];
    const activeRestriction = restrictions.find((item) => (
        String(item?.status || item?.restriction_status).toUpperCase() === "ACTIVE"
    ));
    const userRestrictionValues = Array.isArray(user?.active_restrictions)
        ? user.active_restrictions
        : Array.isArray(user?.active_restriction)
            ? user.active_restriction
            : [];
    const userActiveRestriction = userRestrictionValues.find((item) => (
        String(item?.status || item?.restriction_status || "ACTIVE").toUpperCase() === "ACTIVE"
    ));
    const restrictionType = activeRestriction?.restriction_type
        || userActiveRestriction?.restriction_type
        || user?.active_restriction_type
        || user?.active_restriction?.restriction_type;
    const restrictionTypeLabel = restrictionType
        ? t(`applyStrike.restriction.types.${restrictionType}.label`, formatStatus(restrictionType))
        : t("userManagement.restrictionNone");

    // Stripe Connect status (ENABLED is the only state that satisfies the checklist)
    const stripeStatus = STRIPE_STATUSES.includes(user?.stripe_connect_status) ? user.stripe_connect_status : "NOT_CONNECTED";

    // Address may come as an object or a plain string
    const hasAddress = (addr) => (
        !!addr && (typeof addr === "string" ? addr.trim() !== "" : Object.keys(addr).length > 0)
    );

    // Renders an address block, falling back to the default (store address) text when not configured
    const renderAddress = (addr, fallbackText) => {
        if (!hasAddress(addr)) {
            return <span className="udr-cell-value">{fallbackText}</span>;
        }
        if (typeof addr === "string") {
            return <span className="udr-cell-value udr-address">{addr}</span>;
        }
        return (
            <span className="udr-cell-value udr-address">
                {addr.addressLine1 || "—"}<br />
                {addr.postal_code} {addr.city}<br />
                {addr.province} {addr.country}
            </span>
        );
    };

    // Checklist for seller requirements
    const checklistItems = isSeller
        ? [
            {
                key: "account",
                label: t("userDetails.checklistAccount"),
                desc: t("userDetails.checklistAccountDesc"),
                done: !!(user.user_name && user.email),
            },
            {
                key: "store",
                label: t("userDetails.checklistStore"),
                desc: t("userDetails.checklistStoreDesc"),
                done: !!user.store_name,
            },
            {
                key: "store_address",
                label: t("userDetails.checklistStoreAddress"),
                desc: t("userDetails.checklistStoreAddressDesc"),
                done: hasAddress(user.store_address),
            },
            {
                key: "vat",
                label: t("userDetails.checklistVat"),
                desc: t("userDetails.checklistVatDesc"),
                done: !!user.vat_number,
            },
            {
                key: "kyc",
                label: t("userDetails.checklistKyc"),
                desc: t("userDetails.checklistKycDesc"),
                done: !!user.kyc_no,
            },
            {
                key: "stripe",
                label: t("userDetails.checklistStripe"),
                desc: t("userDetails.checklistStripeDesc"),
                done: stripeStatus === "ENABLED",
                badgeText: t(`userDetails.stripeStatus.${stripeStatus}`),
                badgeClass: STRIPE_BADGE_CLASS[stripeStatus],
            },
        ]
        : [];

    const allRequirementsMet = checklistItems.every((item) => item.done);
    const completedCount = checklistItems.filter((item) => item.done).length;
    const progressPercent = checklistItems.length ? Math.round((completedCount / checklistItems.length) * 100) : 0;

    return (
        <div className="udr-page">
            {/* Page Header */}
            <div className="udr-topbar">
                <button className="udr-back-btn" onClick={() => navigate("/users")}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8" />
                    </svg>
                </button>
                <div>
                    <h1 className="udr-page-title">{isSeller ? t("userDetails.sellerDetailsTitle") : t("userDetails.userDetailsTitle")}</h1>
                    {isSeller && <p className="udr-page-subtitle">{t("userDetails.pageSubtitle").replace("{{role}}", isSeller ? "seller" : "user")}</p>}
                </div>
            </div>

            <div className="udr-layout">
                {/* ── LEFT PANEL ── */}
                <div className="udr-left">
                    <div className="udr-profile-card">
                        {/* Profile header */}
                        <div className="udr-profile-header">
                            <img
                                src={user?.profile_pic ? `${REACT_APP_IMAGE_URL}${user.profile_pic}` : DefaultImg}
                                alt="Profile"
                                className="udr-avatar"
                            />
                            <div className="udr-profile-meta">
                                <div className="udr-name-row">
                                    <h2 className="udr-username">{user.name || user.user_name || "—"}</h2>
                                    {isSeller && (
                                        <span className={`udr-badge ${statusClassMap[user?.seller_status] || ""}`}>
                                            {formatStatus(user?.seller_status)}
                                        </span>
                                    )}
                                </div>

                                {user.id && user.role == "seller" && <p className="udr-seller-id">{t("userDetails.sellerIdLabel")}: SEL-{String(user.id)}</p>}
                            </div>
                        </div>

                        <div className="udr-divider" />

                        {/* Info grid */}
                        <div className="udr-info-grid">
                            <div className="udr-info-cell">
                                <span className="udr-cell-label">{t("userDetails.infoFullName")}</span>
                                <span className="udr-cell-value">{user.name || user.user_name || "—"}</span>
                            </div>
                            <div className="udr-info-cell">
                                <span className="udr-cell-label">{t("userDetails.infoEmail")}</span>
                                <span className="udr-cell-value udr-email-value">
                                    {user.email || "—"}
                                    <span
                                        className="udr-email-verification-icon"
                                        title={user.email_verify ? "Email verified" : "Email not verified"}
                                        aria-label={user.email_verify ? "Email verified" : "Email not verified"}
                                    >
                                        {user.email_verify ? <CheckIcon /> : <CrossIcon />}
                                    </span>
                                </span>
                            </div>
                            <div className="udr-info-cell">
                                <span className="udr-cell-label">{t("userDetails.infoPhone")}</span>
                                <span className="udr-cell-value">
                                    {user.country_code && user.phone_no ? `${user.country_code} ${user.phone_no}` : "—"}
                                </span>
                            </div>
                            <div className="udr-info-cell">
                                <span className="udr-cell-label">{t("userDetails.infoRole")}</span>
                                <span className="udr-cell-value">{user.role?.toUpperCase() || "—"}</span>
                            </div>
                            <div className="udr-info-cell">
                                <span className="udr-cell-label">{t("userDetails.infoCreatedAt")}</span>
                                <span className="udr-cell-value">
                                    {formatDateTime(user.created_at)}
                                </span>
                            </div>
                            <div className="udr-info-cell">
                                <span className="udr-cell-label">{t("userDetails.infoActiveRestriction")}</span>
                                <span className={`udr-badge ${restrictionType ? "cancelled" : "approved"}`}>
                                    {restrictionTypeLabel}
                                </span>
                            </div>

                            {isSeller && (
                                <>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoSellerApprovalStatus")}</span>
                                        <span className={`udr-badge ${statusClassMap[user?.seller_status] || ""}`}>
                                            {formatStatus(user?.seller_status)}
                                        </span>
                                    </div>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoStoreName")}</span>
                                        <span className="udr-cell-value">{user.store_name || "—"}</span>
                                    </div>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoPromoted")}</span>
                                        <span className={`udr-cell-value ${user.promoted ? "udr-promoted--yes" : "udr-promoted--no"}`}>
                                            {user.promoted ? t("userDetails.promotedYes") : t("userDetails.promotedNo")}
                                        </span>
                                    </div>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoStoreAddress")}</span>
                                        {renderAddress(user.store_address, "—")}
                                    </div>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoShippingAddress")}</span>
                                        {renderAddress(user.address, t("userDetails.addressDefaultStore"))}
                                    </div>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoReturnAddress")}</span>
                                        {renderAddress(user.return_address, t("userDetails.addressDefaultStore"))}
                                    </div>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoVatNumber")}</span>
                                        <span className="udr-cell-value">{user.vat_number || "—"}</span>
                                    </div>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoKycNumber")}</span>
                                        <span className="udr-cell-value">{user.kyc_no || "—"}</span>
                                    </div>
                                    <div className="udr-info-cell">
                                        <span className="udr-cell-label">{t("userDetails.infoStripeAccount")}</span>
                                        <span className="udr-cell-value">{user.account_id || "—"}</span>
                                    </div>

                                    {user.label && (
                                        <div className="udr-info-cell udr-info-cell--full">
                                            <span className="udr-cell-label">{t("userDetails.infoPerformance")}</span>
                                            <div className="udr-perf-row">
                                                <span className={`udr-perf-badge udr-perf-badge--${user.label?.toLowerCase().replace(/\s/g, "-")}`}>
                                                    {user.label}
                                                </span>
                                                {user.label !== "Not enough data yet" && user.conversion_rate && (
                                                    <span className="udr-conversion-rate">{user.conversion_rate}</span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {showCompanyLogo && (
                                        <div className="udr-info-cell udr-info-cell--full">
                                            <span className="udr-cell-label">{t("userDetails.infoCompanyLogo")}</span>
                                            <img
                                                src={`${REACT_APP_IMAGE_URL}${user.company_logo}`}
                                                alt="Company logo"
                                                className="udr-company-logo"
                                            />
                                        </div>
                                    )}

                                    {user.promoted && user.bio && (
                                        <div className="udr-info-cell udr-info-cell--full">
                                            <span className="udr-cell-label">{t("userDetails.infoBio")}</span>
                                            <span className="udr-cell-value udr-bio">{user.bio}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            {user.role === "buyer" && user.address && (
                                <div className="udr-info-cell udr-info-cell--full">
                                    <span className="udr-cell-label">{t("userDetails.infoAddress")}</span>
                                    <span className="udr-cell-value udr-address">
                                        {user.address.addressLine1 || user.address.street || "—"}
                                        {user.address.housenumber && `, ${user.address.housenumber}`}
                                        <br />{user.address.postal_code} {user.address.city}
                                        <br />{user.address.province ? `${user.address.province} ` : ""}{user.address.country}
                                    </span>
                                </div>
                            )}
                        </div>

                        {false && user.role == "seller" &&
                            <div className="udr-admin-note">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="12" r="10" stroke="#3b82f6" strokeWidth="1.8" />
                                    <path d="M12 8v4M12 16v.01" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" />
                                </svg>
                                <div>
                                    <p className="udr-note-title">{t("userDetails.adminNoteTitle")}</p>
                                    <p className="udr-note-text">
                                        {t("userDetails.adminNoteText")}
                                    </p>
                                </div>
                            </div>}
                    </div>
                </div>

                {/* ── RIGHT PANEL ── */}
                {isSeller && (
                    <div className="udr-right">
                        {/* Checklist card */}
                        <div className="udr-checklist-card">
                            <div className="udr-checklist-header">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <rect x="3" y="3" width="18" height="18" rx="3" stroke="#f59e0b" strokeWidth="1.8" />
                                    <path d="M7 12l3 3 7-7" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <div>
                                    <h3 className="udr-checklist-title">{t("userDetails.checklistCardTitle")}</h3>
                                    <p className="udr-checklist-subtitle">{t("userDetails.checklistCardSubtitle")}</p>
                                </div>
                            </div>

                            <div className="udr-progress">
                                <div className="udr-progress-top">
                                    <span>{t("userDetails.checklistProgress")}</span>
                                    <span>{completedCount}/{checklistItems.length}</span>
                                </div>
                                <div className="udr-progress-track">
                                    <div
                                        className={`udr-progress-fill${allRequirementsMet ? " udr-progress-fill--done" : ""}`}
                                        style={{ width: `${progressPercent}%` }}
                                    />
                                </div>
                            </div>

                            <div className="udr-checklist-list">
                                {checklistItems.map((item) => (
                                    <div key={item.key} className="udr-checklist-item">
                                        <div className="udr-checklist-left">
                                            {item.done ? <CheckIcon /> : <CrossIcon />}
                                            <div>
                                                <p className="udr-checklist-name">{item.label}</p>
                                                <p className="udr-checklist-desc">{item.desc}</p>
                                            </div>
                                        </div>
                                        <div className="udr-checklist-right">
                                            <span className={`udr-req-badge ${item.badgeClass || (item.done ? "udr-req-badge--done" : "udr-req-badge--missing")}`}>
                                                {item.badgeText || (item.done ? t("userDetails.checklistCompleted") : t("userDetails.checklistMissing"))}
                                            </span>
                                            {/* <ChevronIcon /> */}
                                        </div>
                                    </div>
                                ))}
                            </div>
                                {console.log("allRequirementsMet")}
                            {!allRequirementsMet && (
                                <div className="udr-req-warning">
                                    <WarnIcon />
                                    <div>
                                        <p className="udr-req-warning-title">{t("userDetails.requirementsMissingTitle")}</p>
                                        <p className="udr-req-warning-text">
                                            {/* This seller is missing some required information.<br />
                                            Selling and payout will be blocked until all requirements are met. */}
                                            {t("userDetails.requirementsMissingText")}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>


                        {SHOW_SELLER_REVIEW_ACTIONS && user?.seller_status !== "SELLER_ACTIVE" &&
                            <div className="udr-actions-card">
                                <div className="udr-actions-header">
                                    <h3 className="udr-actions-title">{t("userDetails.adminReviewTitle")}</h3>
                                    <p className="udr-actions-subtitle">{t("userDetails.adminReviewSubtitle")}</p>
                                </div>

                                {userPermissions?.users?.edit && user?.seller_status !== "SELLER_ACTIVE" && (
                                    <button
                                        type="button"
                                        className={`udr-action-btn udr-action-btn--approve${(isLoading && !rejectReasonModal || !allRequirementsMet) ? '-disabled' : ''}`}
                                        onClick={() => handleApprove("SELLER_ACTIVE")}
                                        disabled={isLoading && !rejectReasonModal || !allRequirementsMet}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <div>
                                            <p className="udr-btn-label">{t("userDetails.approveSellerLabel")}</p>
                                            <p className="udr-btn-sublabel">{t("userDetails.approveSellerSublabel")}</p>
                                        </div>
                                    </button>
                                )}

                                {userPermissions?.users?.edit && (user?.seller_status == "SELLER_REGISTERED" || user?.seller_status == "SELLER_EMAIL_VERIFIED") && (
                                    <button
                                        type="button"
                                        className="udr-action-btn udr-action-btn--reject"
                                        onClick={() => setRejectReasonModal(true)}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                            <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                        </svg>
                                        <div>
                                            <p className="udr-btn-label">{t("userDetails.rejectSellerLabel")}</p>
                                            <p className="udr-btn-sublabel">{t("userDetails.rejectSellerSublabel")}</p>
                                        </div>
                                    </button>
                                )}
                            </div>}
                    </div>
                )}

            </div>
            <UserStrike userData={user} />

            {/* Reject Modal */}
            {rejectReasonModal && (<Modal open={rejectReasonModal} onClose={() => setRejectReasonModal(false)}>
                <ModalDialog>
                    <DialogTitle>{t("userDetails.rejectModalTitle")}</DialogTitle>
                    <DialogContent>
                        <div className="udr-modal-body">
                            <label htmlFor="udr-reason" className="udr-modal-label">
                                {t("userDetails.rejectReasonLabel")}
                            </label>
                            <textarea
                                id="udr-reason"
                                className="udr-modal-textarea"
                                placeholder={t("userDetails.rejectReasonPlaceholder")}
                                rows="5"
                                value={sellerRejectReason}
                                onChange={(e) => setSellerRejectReason(e.target.value)}
                            />
                        </div>
                    </DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div className="udr-modal-footer">
                            <button
                                type="button"
                                className="udr-modal-cancel"
                                onClick={() => setRejectReasonModal(false)}
                            >
                                {t("userDetails.rejectCancelButton")}
                            </button>
                            <button
                                type="button"
                                className={`udr-modal-confirm ${(!sellerRejectReason.trim() || isLoading) ? "udr-modal-confirm--disabled" : ""}`}
                                onClick={() => handleApprove("SELLER_REJECTED")}
                                disabled={!sellerRejectReason.trim() || isLoading}
                            >
                                {t("userDetails.rejectConfirmButton")}
                            </button>
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>)}
        </div>
    );
};

export default UserDetails;
